<template>
  <TeleportComponent to="#base-modal-portal">
    <div class="base-modal-overlay" @click="requestDismiss" @keydown.esc="requestDismiss">
      <div
        :id="modalId"
        class="base-modal"
        :class="[{ 'base-modal__form': type === 'form' }, modalClass]"
        @click.stop="emit('click', $event)"
      >
        <Space :space="12">
          <Space v-if="!hideTitle" direction="columns" align-x="between">
            <div class="base-modal-title"><slot name="title" /></div>
            <IconButton class="base-modal-close" name="close" type="info" @click="emit('close')" />
          </Space>
          <div class="base-modal-content"><slot /></div>
          <div v-if="$slots.actions" class="base-modal-actions"><slot name="actions" /></div>
        </Space>
      </div>
    </div>
  </TeleportComponent>
</template>

<script lang="ts" setup>
  import { useEventListener } from '@vueuse/core'
  import { Teleport as TeleportComponent } from 'vue'

  import { IconButton } from '@/components/IconButton'
  import { Space } from '@/components/Space'

  type Props = {
    modalClass?: string
    modalId?: string
    hideTitle?: boolean
    blockCloseOnEscape?: boolean
    dismissible?: boolean
    type?: 'default' | 'form'
  }

  const props = withDefaults(defineProps<Props>(), { dismissible: true })
  const emit = defineEmits(['confirm', 'close', 'click'])
  const requestDismiss = (event: Event) => {
    if (!props.dismissible || (event instanceof KeyboardEvent && props.blockCloseOnEscape)) {
      event.preventDefault()
    } else {
      emit('close', event)
    }
  }

  useEventListener(document, 'keydown', event => {
    if (event.key === 'Enter') {
      emit('confirm', event)
    }
    if (event.key === 'Escape') {
      requestDismiss(event)
    }
  })
</script>

<style>
  .base-modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 99993;
    background-color: rgb(0 0 0 / 30%);
  }

  .base-modal__form {
    box-sizing: border-box;
    width: 100% !important;
    max-width: 578px;
    overflow: unset !important;
  }

  .base-modal {
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 9001;
    width: 480px;
    max-height: 80%;
    padding: 25px;
    overflow: auto;
    background-color: #fff;
    border-radius: 4px;
    box-shadow: 2px 2px 8px rgb(0 0 0 / 30%);
    transform: translate(-50%, -50%);
  }

  .base-modal-title {
    padding-right: 28px;
    font-family: Montserrat-Medium;
    font-size: 20px;
    line-height: 28px;
    color: var(--color-primary-text);
    letter-spacing: -1.7%;
  }

  .base-modal-content {
    font-size: 12px;
    line-height: 20px;
    color: var(--color-primary-text);
  }

  .base-modal-actions {
    display: flex;
    flex-direction: row;
    gap: 16px;
    align-items: center;
    justify-content: flex-end;
  }
</style>
